import type { Promo } from '@sunrise-ui/api/events';
import { create } from 'zustand';

const generateSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

type AttendeeFormData = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  ticketTypeId: string;
  smsConsent?: boolean;
};

type QuestionnaireFormData = {
  formId: string;
  formValues: Record<string, unknown>;
};

type CheckoutFormState = {
  // Session identifier (unique per checkout session)
  sessionId: string;

  // Screen 1: Ticket Selection
  ticketQuantities: Map<string, number>;
  appliedPromo: Promo | undefined;

  // Screen 2: Attendee Details
  attendee: AttendeeFormData | undefined;
  questionnaireData: QuestionnaireFormData | undefined;

  // Screen 3: Payment
  clientSecret: string | undefined;

  // Ticket selection actions
  updateTicketQuantities: (ticketQuantities: Map<string, number>) => void;
  setAppliedPromo: (promo?: Promo) => void;

  // Attendee actions
  setAttendee: (attendee: AttendeeFormData) => void;
  setQuestionnaireData: (data: QuestionnaireFormData | undefined) => void;

  // Payment actions
  setClientSecret: (clientSecret: string | undefined) => void;

  // Reset
  resetForm: () => void;
};

const getInitialState = () =>
  ({
    sessionId: generateSessionId(),
    ticketQuantities: new Map<string, number>(),
    appliedPromo: undefined,
    attendee: undefined,
    questionnaireData: undefined,
    clientSecret: undefined,
  }) as const;

export const useCheckoutFormStore = create<CheckoutFormState>((set) => ({
  ...getInitialState(),

  updateTicketQuantities: (ticketQuantities) => {
    set({ ticketQuantities });
  },

  setAppliedPromo: (promo) => {
    set({ appliedPromo: promo });
  },

  setAttendee: (attendee) => {
    set({ attendee });
  },

  setQuestionnaireData: (data) => {
    set({ questionnaireData: data });
  },

  setClientSecret: (clientSecret) => {
    set({ clientSecret });
  },

  resetForm: () => {
    set(getInitialState());
  },
}));
